import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Download, Upload, Trash2, Loader2, FileSpreadsheet, Plane, AlertTriangle, Check, X, RefreshCw, Ban, Edit } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePermissions } from "@/context/PermissionContext";
import DeleteWithBackupDialog from "@/components/DeleteWithBackupDialog";

export default function ImportExportPage() {
  const { hasPermission } = usePermissions();
  const [flightCount, setFlightCount] = useState(0);
  const [canceledCount, setCanceledCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteFlightsOpen, setDeleteFlightsOpen] = useState(false);
  const [deleteCanceledOpen, setDeleteCanceledOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [syncMode, setSyncMode] = useState(false);
  const fileInputRef = useRef(null);
  const opsFileInputRef = useRef(null);
  const [opsUploading, setOpsUploading] = useState(false);
  const [opsResult, setOpsResult] = useState(null);
  const [opsConfirmOpen, setOpsConfirmOpen] = useState(false);
  const [pendingOpsFile, setPendingOpsFile] = useState(null);

  // Permission checks
  const canImport = hasPermission("import_export", "import");
  const canExport = hasPermission("import_export", "export");
  const canDeleteAll = hasPermission("import_export", "delete_all");

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const [flightsRes, canceledRes] = await Promise.all([
        api.get("/flights?status=upcoming").catch(() => ({ data: [] })),
        api.get("/canceled-flights").catch(() => ({ data: [] }))
      ]);
      
      // Get total flights count
      const allFlights = await api.get("/flights").catch(() => ({ data: [] }));
      setFlightCount(allFlights.data?.length || 0);
      setCanceledCount(canceledRes.data?.length || 0);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/flights/export/xlsx", {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `flights_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Flights exported successfully");
    } catch (error) {
      toast.error("Failed to export flights");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      toast.error("Please select an XLS or XLSX file");
      return;
    }

    setUploading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(`/flights/import?sync_mode=${syncMode}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setImportResult(response.data);
      toast.success(response.data.message);
      fetchCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import flights");
      setImportResult({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleOpsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
      toast.error("Please select an XLS or XLSX file");
      return;
    }
    setPendingOpsFile(file);
    setOpsConfirmOpen(true);
  };

  const handleOpsImport = async () => {
    const file = pendingOpsFile;
    setOpsConfirmOpen(false);
    if (!file) return;
    setOpsUploading(true);
    setOpsResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/flights/import-ops", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setOpsResult(response.data.summary);
      toast.success(`Imported ${response.data.summary.imported} legs for ${response.data.summary.years.join(', ')}`);
      fetchCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ops import failed");
      setOpsResult({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setOpsUploading(false);
      setPendingOpsFile(null);
      if (opsFileInputRef.current) opsFileInputRef.current.value = "";
    }
  };

  const handleDeleteAllFlights = async () => {
    try {
      const response = await api.delete("/flights/all");
      toast.success(response.data.message);
      fetchCounts();
    } catch (error) {
      toast.error("Failed to delete flights");
      throw error; // Re-throw for the dialog to handle
    }
  };

  const handleDeleteAllCanceled = async () => {
    try {
      const response = await api.delete("/canceled-flights/all");
      toast.success(response.data.message);
      fetchCounts();
    } catch (error) {
      toast.error("Failed to delete canceled flights");
      throw error; // Re-throw for the dialog to handle
    }
  };

  return (
    <div className="animate-fade-in" data-testid="import-export-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] font-['Outfit']">Import / Export Flights</h1>
          <p className="text-[#64748b] mt-1">Import flights from XLS or export existing flights</p>
        </div>
        <div className="flex gap-3">
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-lg px-4 py-2">
            {flightCount} Active Flights
          </Badge>
          <Badge variant="secondary" className="bg-red-100 text-red-700 text-lg px-4 py-2">
            {canceledCount} Canceled
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Section */}
        {canExport && (
          <Card className="border-[#e2e8f0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <Download size={20} className="text-[#d4af37]" />
                Export Flights
              </CardTitle>
              <CardDescription>
                Download all active flights as an Excel file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleExport}
                disabled={exporting || flightCount === 0}
                className="w-full bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] h-12"
                data-testid="export-flights-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={20} className="mr-2" />
                    Export to XLSX ({flightCount} flights)
                  </>
                )}
              </Button>
              
              <div className="mt-4 p-3 bg-[#f8fafc] rounded-lg">
                <p className="text-sm font-medium text-[#0f172a] mb-2">Export Format:</p>
                <p className="text-xs text-[#64748b] font-mono">
                  DATE | Flight No | REGistration | TIME | ACTUAL | origine | destination | Parking | Pilot In Command | N°Crew | type of flight | AOC | Pax IN | Pax OUT | Cargo Kg | notes
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Section */}
        {canImport && (
          <Card className="border-[#e2e8f0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <Upload size={20} className="text-green-600" />
                Import Flights
              </CardTitle>
              <CardDescription>
                Upload flights from XLS/XLSX file with smart duplicate handling
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="import-file-input"
              />
              
              {/* Sync Mode Toggle */}
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                <div className="flex items-center gap-2">
                  <RefreshCw size={18} className="text-amber-600" />
                  <div>
                    <Label htmlFor="sync-mode" className="text-sm font-medium text-amber-800 cursor-pointer">
                      Sync Mode
                    </Label>
                    <p className="text-xs text-amber-700 mt-0.5">
                      When enabled, flights missing from the imported file will be canceled
                    </p>
                  </div>
                </div>
                <Switch
                  id="sync-mode"
                  checked={syncMode}
                  onCheckedChange={setSyncMode}
                  data-testid="sync-mode-toggle"
                />
              </div>

              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white h-12"
                data-testid="import-flights-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={20} className="mr-2" />
                    Import from XLS File {syncMode && "(Sync Mode)"}
                  </>
                )}
              </Button>

            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-2">✨ Smart Import Features:</p>
              <ul className="text-xs text-green-700 space-y-1">
                <li className="flex items-center gap-1"><Check size={12} /> <strong>Duplicate Detection:</strong> Skips identical flights</li>
                <li className="flex items-center gap-1"><Edit size={12} /> <strong>Auto-Update:</strong> Updates existing flights if details changed</li>
                <li className="flex items-center gap-1"><Ban size={12} /> <strong>Sync Mode:</strong> Cancels flights not in the import file</li>
                <li className="flex items-center gap-1"><Plane size={12} /> <strong>Auto-Fill:</strong> Aircraft & airport data from database</li>
              </ul>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`mt-4 p-3 rounded-lg ${importResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                {importResult.error ? (
                  <div className="flex items-center gap-2 text-red-700">
                    <X size={16} />
                    <span className="text-sm">{importResult.error}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <Check size={16} />
                      <span className="text-sm font-medium">{importResult.message}</span>
                    </div>
                    
                    {/* Import Statistics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 mb-3">
                      {importResult.imported > 0 && (
                        <div className="bg-blue-100 p-2 rounded text-center">
                          <div className="text-lg font-bold text-blue-700">{importResult.imported}</div>
                          <div className="text-xs text-blue-600">New</div>
                        </div>
                      )}
                      {importResult.updated > 0 && (
                        <div className="bg-amber-100 p-2 rounded text-center">
                          <div className="text-lg font-bold text-amber-700">{importResult.updated}</div>
                          <div className="text-xs text-amber-600">Updated</div>
                        </div>
                      )}
                      {importResult.skipped > 0 && (
                        <div className="bg-gray-100 p-2 rounded text-center">
                          <div className="text-lg font-bold text-gray-700">{importResult.skipped}</div>
                          <div className="text-xs text-gray-600">Skipped</div>
                        </div>
                      )}
                      {importResult.canceled > 0 && (
                        <div className="bg-red-100 p-2 rounded text-center">
                          <div className="text-lg font-bold text-red-700">{importResult.canceled}</div>
                          <div className="text-xs text-red-600">Canceled</div>
                        </div>
                      )}
                    </div>

                    {/* Processed Dates */}
                    {importResult.dates_processed?.length > 0 && (
                      <div className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">Dates processed:</span> {importResult.dates_processed.join(', ')}
                      </div>
                    )}

                    {importResult.columns_mapped && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                        <p className="font-medium text-blue-700 mb-1">Columns Detected:</p>
                        <div className="grid grid-cols-2 gap-1 text-blue-600">
                          {Object.entries(importResult.columns_mapped).map(([target, source]) => (
                            <div key={target}>
                              <span className="font-medium">{target}:</span> "{source}"
                            </div>
                          ))}
                        </div>
                        {importResult.columns_found && (
                          <p className="mt-2 text-gray-500">
                            <span className="font-medium">All columns in file:</span> {importResult.columns_found.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-red-600 font-medium">Errors:</p>
                        <ul className="text-xs text-red-600 mt-1">
                          {importResult.errors.map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Ops Planning Yearly Import */}
        {canImport && (
          <Card className="border-[#e2e8f0]" data-testid="ops-import-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <FileSpreadsheet size={20} className="text-blue-600" />
                Import Yearly Ops Planning
              </CardTitle>
              <CardDescription>
                Upload a full-year ops Excel (POS / SCH / ATA-ATD / IN-OUT layout). Rotations are auto-paired.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={opsFileInputRef}
                type="file"
                accept=".xls,.xlsx"
                onChange={handleOpsFileSelect}
                className="hidden"
                data-testid="ops-import-file-input"
              />

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                <p className="text-sm font-medium text-blue-800 mb-2">How it works:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li className="flex items-start gap-1"><Plane size={12} className="mt-0.5 shrink-0" /> Arrivals paired with departures (same reg) into rotations; unpaired legs are excluded</li>
                  <li className="flex items-start gap-1"><Check size={12} className="mt-0.5 shrink-0" /> 0-pax passenger flights → Positioning; cargo weight from the Kg column</li>
                  <li className="flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> <strong>Replaces all existing flights for each year found in the file</strong></li>
                </ul>
              </div>

              <Button
                onClick={() => opsFileInputRef.current?.click()}
                disabled={opsUploading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                data-testid="ops-import-btn"
              >
                {opsUploading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Importing...</>
                ) : (
                  <><Upload size={20} className="mr-2" /> Import Ops Planning File</>
                )}
              </Button>

              <a
                href="/ops_planning_template.xlsx"
                download
                className="mt-3 flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                data-testid="ops-template-download"
              >
                <Download size={14} /> Download blank template
              </a>

              {opsResult && (
                <div className={`mt-4 p-3 rounded-lg ${opsResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="ops-import-result">
                  {opsResult.error ? (
                    <div className="flex items-center gap-2 text-red-700"><X size={16} /><span className="text-sm">{opsResult.error}</span></div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <Check size={16} />
                        <span className="text-sm font-medium">Imported {opsResult.years?.join(', ')}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="bg-blue-100 p-2 rounded text-center"><div className="text-lg font-bold text-blue-700">{opsResult.imported}</div><div className="text-xs text-blue-600">Legs imported</div></div>
                        <div className="bg-emerald-100 p-2 rounded text-center"><div className="text-lg font-bold text-emerald-700">{opsResult.rotations}</div><div className="text-xs text-emerald-600">Rotations</div></div>
                        <div className="bg-gray-100 p-2 rounded text-center"><div className="text-lg font-bold text-gray-700">{opsResult.excluded_unpaired}</div><div className="text-xs text-gray-600">Excluded</div></div>
                        <div className="bg-amber-100 p-2 rounded text-center"><div className="text-lg font-bold text-amber-700">{opsResult.replaced_existing}</div><div className="text-xs text-amber-600">Replaced</div></div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* No Permission Message */}
        {!canExport && !canImport && (
          <Card className="border-[#e2e8f0] col-span-2">
            <CardContent className="py-8 text-center">
              <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
              <p className="text-lg font-medium text-[#0f172a]">No Import/Export Permissions</p>
              <p className="text-[#64748b] mt-2">Contact an administrator to request access.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Data Section */}
      {canDeleteAll && (
        <Card className="border-red-200 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-red-700">
              <Trash2 size={20} />
              Clear Data
            </CardTitle>
            <CardDescription>
              Delete all flights or canceled flights to start fresh
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={() => setDeleteFlightsOpen(true)}
                disabled={flightCount === 0}
                className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-12"
                data-testid="delete-all-flights-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Delete All Active Flights ({flightCount})
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteCanceledOpen(true)}
                disabled={canceledCount === 0}
                className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-12"
                data-testid="delete-all-canceled-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Delete All Canceled Flights ({canceledCount})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Format Info */}
      <Card className="border-[#e2e8f0] mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
            <FileSpreadsheet size={20} />
            Import File Format
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0f172a] text-white">
                  <th className="p-2 text-left">Column</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Example</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b"><td className="p-2 font-mono">DATE</td><td className="p-2">Flight date (YYYY-MM-DD)</td><td className="p-2 text-[#64748b]">2026-01-27</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">Flight No</td><td className="p-2">Flight number</td><td className="p-2 text-[#64748b]">ABC123</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">REGistration</td><td className="p-2">Aircraft registration</td><td className="p-2 text-[#64748b]">OO-ABC</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">TIME</td><td className="p-2">Scheduled time (STD/STA)</td><td className="p-2 text-[#64748b]">14:30</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">ACTUAL</td><td className="p-2">Actual time (ATD/ATA)</td><td className="p-2 text-[#64748b]">14:45</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">origine</td><td className="p-2">Origin ICAO code</td><td className="p-2 text-[#64748b]">EBCI</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">destination</td><td className="p-2">Destination ICAO code</td><td className="p-2 text-[#64748b]">LFPG</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">Parking</td><td className="p-2">Parking position</td><td className="p-2 text-[#64748b]">Stand 12</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">Pilot In Command</td><td className="p-2">Captain name</td><td className="p-2 text-[#64748b]">John Smith</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">N°Crew</td><td className="p-2">Number of crew</td><td className="p-2 text-[#64748b]">3</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">type of flight</td><td className="p-2">Flight type</td><td className="p-2 text-[#64748b]">Charter</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">AOC</td><td className="p-2">Air Operator Certificate</td><td className="p-2 text-[#64748b]">BE.AOC.0123</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">Pax IN</td><td className="p-2">Passengers boarding</td><td className="p-2 text-[#64748b]">8</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">Pax OUT</td><td className="p-2">Passengers deplaning</td><td className="p-2 text-[#64748b]">0</td></tr>
                <tr className="border-b"><td className="p-2 font-mono">Cargo Kg</td><td className="p-2">Cargo weight in kg</td><td className="p-2 text-[#64748b]">250</td></tr>
                <tr className="border-b bg-[#f8fafc]"><td className="p-2 font-mono">notes</td><td className="p-2">Additional notes</td><td className="p-2 text-[#64748b]">VIP flight</td></tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete All Flights Dialog with Backup */}
      <DeleteWithBackupDialog
        open={deleteFlightsOpen}
        onOpenChange={setDeleteFlightsOpen}
        title="Delete All Active Flights"
        description="Are you sure you want to delete ALL active flights? This action cannot be undone."
        itemCount={flightCount}
        onConfirmDelete={handleDeleteAllFlights}
        leavePath="/dashboard"
      />

      {/* Delete All Canceled Flights Dialog with Backup */}
      <DeleteWithBackupDialog
        open={deleteCanceledOpen}
        onOpenChange={setDeleteCanceledOpen}
        title="Delete All Canceled Flights"
        description="Are you sure you want to delete ALL canceled flights? This action cannot be undone."
        itemCount={canceledCount}
        onConfirmDelete={handleDeleteAllCanceled}
        leavePath="/dashboard"
      />

      {/* Ops Import Confirmation */}
      <AlertDialog open={opsConfirmOpen} onOpenChange={setOpsConfirmOpen}>
        <AlertDialogContent data-testid="ops-import-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Import ops planning file?</AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>replace all existing flights</strong> for every year found in
              "{pendingOpsFile?.name}". Rotations are auto-paired and unpaired legs excluded.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingOpsFile(null)} data-testid="ops-import-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleOpsImport} className="bg-blue-600 hover:bg-blue-700" data-testid="ops-import-confirm-btn">Import & Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
